import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Upload, CheckCircle2, AlertTriangle, FileSpreadsheet, ArrowRight, RotateCcw, Sparkles, Wand2 } from "lucide-react";

const SKIP = "__skip__";

const ENTITIES = [
  { key: "risks", label: "Risks", desc: "title, theme, owner email, inherent & residual scores" },
  { key: "suppliers", label: "Suppliers", desc: "legal name, country, tier, NAICS, DUNS" },
  { key: "controls", label: "Controls", desc: "control id, title, maturity, themes" },
];

const Step = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2">
    <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
      done ? "bg-emerald-600 text-white" : active ? "bg-ek-purple text-white" : "bg-black/10 text-ek-ink/65"}`}>
      {done ? "✓" : n}
    </span>
    <span className={`text-sm font-medium ${active || done ? "text-ek-ink" : "text-ek-ink/65"}`}>{label}</span>
  </div>
);

export default function Onboarding() {
  const [entity, setEntity] = useState("risks");
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [inspect, setInspect] = useState(null);
  const [mapping, setMapping] = useState({});
  const [pendingFile, setPendingFile] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/import/${entity}/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `erio_${entity}_template.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch {
      toast.error("Template download failed");
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/import/${entity}/inspect`, form);
      setInspect(data);
      setMapping(data.suggested_mapping || {});
      setPendingFile(file);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read file");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const runPreview = async () => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", pendingFile);
      form.append("mapping", JSON.stringify(mapping));
      const { data } = await api.post(`/import/${entity}/preview`, form);
      setPreview(data);
      setStep(3);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Preview failed");
    } finally {
      setUploading(false);
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post(`/import/${entity}/commit`, { rows: preview.rows });
      setResult(data);
      setStep(4);
      toast.success(`Imported ${data.created} ${entity}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCommitting(false);
    }
  };

  const reset = () => { setStep(1); setPreview(null); setResult(null); setInspect(null); setPendingFile(null); setMapping({}); };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-5xl px-6 py-12" data-testid="onboarding-page">
        <p className="ek-eyebrow">Self-service onboarding</p>
        <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Import your data</h1>
        <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
          Bring your existing register into ERIO in minutes — download a template, fill it in, upload, review
          the validation, and commit. CSV and Excel (.xlsx) are supported.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md border border-black/5 bg-white px-5 py-3" data-testid="onboarding-steps">
          <Step n={1} label="Choose & download" active={step === 1} done={step > 1} />
          <ArrowRight size={14} className="text-ek-ink/20" />
          <Step n={2} label="Upload" active={step === 2} done={step > 2} />
          <ArrowRight size={14} className="text-ek-ink/20" />
          <Step n={3} label="Review" active={step === 3} done={step > 3} />
          <ArrowRight size={14} className="text-ek-ink/20" />
          <Step n={4} label="Done" active={step === 4} done={step > 4} />
        </div>

        {/* Step 1: choose entity + template */}
        {step === 1 && (
          <div className="mt-6 rounded-md border border-black/5 bg-white p-6" data-testid="step-choose">
            <p className="text-[0.7rem] font-semibold uppercase tracking-wide text-ek-ink/65">What are you importing?</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {ENTITIES.map((en) => (
                <button key={en.key} onClick={() => setEntity(en.key)} data-testid={`entity-${en.key}`}
                  className={`rounded-md border p-4 text-left transition-colors ${
                    entity === en.key ? "border-ek-purple bg-ek-purple/5" : "border-black/10 hover:border-ek-purple/40"}`}>
                  <p className="font-semibold text-ek-ink">{en.label}</p>
                  <p className="mt-1 text-xs text-ek-ink/65">{en.desc}</p>
                </button>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button onClick={downloadTemplate} data-testid="download-template" className="flex items-center gap-2 rounded-full border border-black/15 px-4 py-2 text-sm font-semibold text-ek-ink/70 hover:border-ek-purple hover:text-ek-purple">
                <Download size={15} /> Download {entity} template
              </button>
              <button onClick={() => setStep(2)} data-testid="goto-upload" className="ek-btn flex items-center gap-2">
                Next: upload <ArrowRight size={15} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: upload + AI column mapping */}
        {step === 2 && (
          <div className="mt-6 rounded-md border border-black/5 bg-white p-6" data-testid="step-upload">
            {!inspect ? (
              <>
                <label className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed border-ek-purple/30 bg-ek-purple/5 py-14 text-center transition-colors hover:bg-ek-purple/10">
                  <FileSpreadsheet size={36} className="text-ek-purple" />
                  <span className="text-sm font-semibold text-ek-ink">{uploading ? "Reading file…" : `Upload your ${entity} file`}</span>
                  <span className="text-xs text-ek-ink/65">CSV or Excel (.xlsx) · up to 1000 rows · headers can differ — we'll map them</span>
                  <input type="file" accept=".csv,.xlsx,.xls" onChange={onFile} disabled={uploading} className="hidden" data-testid="file-input" />
                </label>
                <button onClick={() => setStep(1)} className="mt-4 text-sm text-ek-ink/65 hover:text-ek-purple">← Back</button>
              </>
            ) : (
              <div data-testid="mapping-editor">
                <div className="flex items-center gap-2">
                  {inspect.mapping_method === "ai"
                    ? <Sparkles size={16} className="text-ek-purple" />
                    : <Wand2 size={16} className="text-ek-purple" />}
                  <p className="text-sm font-semibold text-ek-ink">
                    Map your columns
                    <span className="ml-2 rounded-full bg-ek-purple/10 px-2 py-0.5 text-[0.65rem] font-semibold uppercase text-ek-purple" data-testid="mapping-method">
                      {inspect.mapping_method === "ai" ? "AI-suggested" : "Auto-matched"}
                    </span>
                  </p>
                </div>
                <p className="mt-1 text-xs text-ek-ink/65">
                  We matched your {inspect.row_count} rows against the {entity} template. Adjust any mapping below, then preview.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {inspect.template_columns.map((col) => {
                    const required = inspect.required.includes(col);
                    return (
                      <div key={col} className="flex items-center gap-3" data-testid={`map-row-${col}`}>
                        <span className="w-40 shrink-0 text-sm text-ek-ink">
                          {col}{required && <span className="text-red-500"> *</span>}
                        </span>
                        <Select value={mapping[col] || SKIP} onValueChange={(v) => setMapping((m) => ({ ...m, [col]: v === SKIP ? null : v }))}>
                          <SelectTrigger data-testid={`map-select-${col}`} className="text-xs"><SelectValue placeholder="— skip —" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SKIP}>— skip —</SelectItem>
                            {inspect.file_headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 flex items-center gap-3">
                  <button onClick={() => { setInspect(null); setPendingFile(null); }} className="rounded-full border border-black/15 px-4 py-2 text-sm text-ek-ink/60 hover:text-ek-purple">Choose another file</button>
                  <button onClick={runPreview} disabled={uploading} data-testid="preview-with-mapping" className="ek-btn flex items-center gap-2">
                    {uploading ? "Validating…" : "Preview import"} <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: review */}
        {step === 3 && preview && (
          <div className="mt-6" data-testid="step-review">
            <div className="flex flex-wrap items-center gap-4 rounded-md border border-black/5 bg-white px-5 py-4">
              <span className="flex items-center gap-2 text-sm"><CheckCircle2 size={16} className="text-emerald-600" /> <b data-testid="valid-count">{preview.summary.valid}</b> valid</span>
              <span className="flex items-center gap-2 text-sm"><AlertTriangle size={16} className="text-red-500" /> <b data-testid="invalid-count">{preview.summary.invalid}</b> with errors</span>
              <span className="text-sm text-ek-ink/65">{preview.summary.total} rows total</span>
              <div className="ml-auto flex gap-2">
                <button onClick={() => setStep(2)} className="rounded-full border border-black/15 px-4 py-1.5 text-sm text-ek-ink/60 hover:text-ek-purple">Re-upload</button>
                <button onClick={commit} disabled={committing || preview.summary.valid === 0} data-testid="commit-button" className="ek-btn">
                  {committing ? "Importing…" : `Import ${preview.summary.valid} valid`}
                </button>
              </div>
            </div>
            <div className="mt-4 overflow-x-auto rounded-md border border-black/5 bg-white" data-testid="preview-table">
              <table className="w-full text-sm">
                <thead className="bg-ek-canvas/70 text-left text-[0.7rem] uppercase tracking-wide text-ek-ink/65">
                  <tr>
                    <th className="px-3 py-2 w-12"></th>
                    {preview.columns.map((c) => <th key={c} className="px-3 py-2">{c}</th>)}
                    <th className="px-3 py-2">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className={`border-t border-black/5 ${r.valid ? "" : "bg-red-50/40"}`}>
                      <td className="px-3 py-2">
                        {r.valid ? <CheckCircle2 size={15} className="text-emerald-600" /> : <AlertTriangle size={15} className="text-red-500" />}
                      </td>
                      {preview.columns.map((c) => <td key={c} className="px-3 py-2 text-ek-ink/70">{r.raw[c] || "—"}</td>)}
                      <td className="px-3 py-2 text-xs text-red-600">{r.errors.join("; ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Step 4: result */}
        {step === 4 && result && (
          <div className="mt-6 rounded-md border border-black/5 bg-white p-8 text-center" data-testid="step-done">
            <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
            <p className="mt-3 text-2xl font-bold text-ek-ink" data-testid="created-count">{result.created} {entity} imported</p>
            {result.skipped > 0 && <p className="mt-1 text-sm text-ek-ink/65">{result.skipped} rows skipped due to validation errors.</p>}
            <button onClick={reset} data-testid="import-again" className="mt-6 inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2 text-sm font-semibold text-ek-ink/70 hover:border-ek-purple hover:text-ek-purple">
              <RotateCcw size={15} /> Import more
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
